import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { getProductApprovals, reviewProduct } from "../../api/adminService";
import { getProductById } from "../../api/productService";
import { ProductFormDialog } from "../BusinessOwner/Products";
import { formatCurrency, formatDate } from "./constants";

const PAGE_SIZE = 12;

const DISCOUNT_TYPE_FIXED = 1;
const DISCOUNT_TYPE_PERCENTAGE = 2;

const VIDEO_PLACEMENT_LABELS = {
  1: "Video first",
  2: "Main photo, then video",
  3: "Photos first",
};

function formatAttribute(a) {
  if (a.value == null || a.value === "") return a.name;
  return `${a.name}: ${a.value}${a.unit ? ` ${a.unit}` : ""}`;
}

const TABS = [
  { value: "pending", label: "Waiting for review", icon: PendingActionsRoundedIcon, color: "warning" },
  { value: "rejected", label: "Rejected", icon: BlockRoundedIcon, color: "error" },
];

export default function ProductApprovals() {
  const [searchParams, setSearchParams] = useSearchParams();
  const storeId = searchParams.get("storeId") || undefined;

  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [notice, setNotice] = useState("");
  // { product: full ProductDto, store: { storeId, type } } - drives the shared product editor.
  const [editing, setEditing] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getProductApprovals({ status, storeId, search: query || undefined, page, pageSize: PAGE_SIZE })
      .then((data) => {
        setItems(data.items);
        setTotalCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load products for review. Is the API running?"))
      .finally(() => setLoading(false));
  }, [status, storeId, query, page]);

  const loadCounts = useCallback(() => {
    Promise.all(TABS.map((tab) => getProductApprovals({ status: tab.value, storeId, page: 1, pageSize: 1 })))
      .then((results) => {
        const next = {};
        TABS.forEach((tab, i) => {
          next[tab.value] = results[i].totalCount;
        });
        setCounts(next);
      })
      .catch(() => {});
  }, [storeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  function refresh() {
    load();
    loadCounts();
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    setQuery(search.trim());
    setPage(1);
  }

  async function handleApprove(product) {
    setBusyId(product.productId);
    try {
      await reviewProduct(product.productId, true);
      refresh();
    } catch {
      setError(`Could not approve "${product.name}".`);
    } finally {
      setBusyId(null);
    }
  }

  async function openEditor(item) {
    setOpeningId(item.productId);
    try {
      const product = await getProductById(item.productId);
      setEditing({ product, store: { storeId: item.storeId, type: item.storeType } });
    } catch {
      setError(`Could not open "${item.name}" for editing.`);
    } finally {
      setOpeningId(null);
    }
  }

  function handleEditorSaved(message) {
    setEditing(null);
    setNotice(message);
    refresh();
  }

  async function handleReject(reason) {
    const product = rejecting;
    setBusyId(product.productId);
    try {
      await reviewProduct(product.productId, false, reason);
      setRejecting(null);
      refresh();
    } catch (err) {
      setError(err?.response?.data || `Could not reject "${product.name}".`);
    } finally {
      setBusyId(null);
    }
  }

  const storeName = storeId && items[0]?.storeId === Number(storeId) ? items[0].storeName : null;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <FactCheckOutlinedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">Product Approvals</Typography>
            <Typography variant="body2" color="text.secondary">
              Products from stores set to "Review first" stay hidden from clients until you approve them.
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="Refresh">
          <IconButton onClick={refresh} sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2.5, overflow: "hidden" }}>
        <Tabs
          value={status}
          onChange={(e, v) => {
            setStatus(v);
            setPage(1);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.value}
                value={tab.value}
                iconPosition="start"
                icon={<Icon sx={{ fontSize: 18 }} />}
                label={
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                    <span>{tab.label}</span>
                    <Chip
                      size="small"
                      label={counts[tab.value] ?? "…"}
                      color={status === tab.value ? tab.color : "default"}
                      variant={status === tab.value ? "filled" : "outlined"}
                      sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                    />
                  </Stack>
                }
                sx={{ minHeight: 52, textTransform: "none" }}
              />
            );
          })}
        </Tabs>

        <Stack
          component="form"
          onSubmit={handleSearchSubmit}
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ p: 2, alignItems: { xs: "stretch", sm: "center" } }}
        >
          <TextField
            placeholder="Search by product or store…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 320 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          {storeId && (
            <Chip
              icon={<StorefrontIcon />}
              label={storeName ? `Store: ${storeName}` : `Store #${storeId}`}
              onDelete={() => {
                setSearchParams({});
                setPage(1);
              }}
              deleteIcon={<CloseRoundedIcon />}
            />
          )}
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 8, textAlign: "center" }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 48, color: "success.main", mb: 1 }} />
          <Typography variant="h6">{status === "pending" ? "All caught up" : "No rejected products"}</Typography>
          <Typography variant="body2" color="text.secondary">
            {status === "pending" ? "There are no products waiting for review." : "Nothing has been rejected here."}
          </Typography>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" },
              alignItems: "start",
            }}
          >
            {items.map((product) => (
              <ReviewCard
                key={product.productId}
                product={product}
                busy={busyId === product.productId}
                opening={openingId === product.productId}
                onEdit={() => openEditor(product)}
                onApprove={() => handleApprove(product)}
                onReject={() => setRejecting(product)}
              />
            ))}
          </Box>
          {totalCount > PAGE_SIZE && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination count={Math.ceil(totalCount / PAGE_SIZE)} page={page} onChange={(e, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}

      {editing && (
        <ProductFormDialog
          store={editing.store}
          product={editing.product}
          onClose={() => setEditing(null)}
          onSaved={handleEditorSaved}
          showTranslations
          secondaryAction={{
            label: "Save & Approve",
            color: "success",
            onAfterSave: () => reviewProduct(editing.product.productId, true),
          }}
        />
      )}

      <RejectDialog
        product={rejecting}
        busy={rejecting && busyId === rejecting.productId}
        onClose={() => setRejecting(null)}
        onSubmit={handleReject}
      />
    </Box>
  );
}

function ReviewCard({ product, busy, opening, onEdit, onApprove, onReject }) {
  const [imageIndex, setImageIndex] = useState(0);
  const images = product.imageUrls || [];
  const hasDiscount = product.discountPrice != null && product.discountPrice < product.price;
  const variants = product.variants || [];
  const attributes = product.attributes || [];
  const isRejected = product.approvalStatus === 3;

  return (
    <Card variant="outlined" sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        onClick={onEdit}
        sx={{ position: "relative", aspectRatio: "4 / 3", bgcolor: "background.default", cursor: "pointer" }}
      >
        {images.length > 0 ? (
          <Box
            component="img"
            src={images[Math.min(imageIndex, images.length - 1)]}
            alt={product.name}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
            <ImageNotSupportedOutlinedIcon />
            <Typography variant="caption">No photos yet</Typography>
          </Stack>
        )}
        <Chip
          size="small"
          label={`${images.length} photo${images.length === 1 ? "" : "s"}`}
          sx={{ position: "absolute", left: 8, top: 8, bgcolor: "rgba(0,0,0,0.55)", color: "#fff" }}
        />
        {product.videoUrl && (
          <Tooltip title="Open video">
            <IconButton
              component="a"
              href={product.videoUrl}
              target="_blank"
              rel="noreferrer"
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.55)", color: "#fff" }}
            >
              <PlayCircleOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {opening && (
          <Stack
            sx={{ position: "absolute", inset: 0, alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.6)" }}
          >
            <CircularProgress size={28} />
          </Stack>
        )}
      </Box>
      {images.length > 1 && (
        <Stack direction="row" spacing={0.75} sx={{ px: 1.5, pt: 1, overflowX: "auto" }}>
          {images.map((url, i) => (
            <Box
              key={url}
              component="img"
              src={url}
              alt=""
              onClick={() => setImageIndex(i)}
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                objectFit: "cover",
                borderRadius: 1,
                cursor: "pointer",
                border: "2px solid",
                borderColor: i === imageIndex ? "primary.main" : "transparent",
              }}
            />
          ))}
        </Stack>
      )}

      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
          <Avatar src={product.storeLogoUrl || undefined} variant="rounded" sx={{ width: 22, height: 22 }}>
            <StorefrontIcon sx={{ fontSize: 14 }} />
          </Avatar>
          <Typography variant="caption" color="text.secondary" noWrap>
            {product.storeName}
            {product.categoryName ? ` · ${product.categoryName}` : ""}
          </Typography>
        </Stack>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          onClick={onEdit}
          sx={{ lineHeight: 1.3, cursor: "pointer", "&:hover": { color: "primary.main" } }}
        >
          {product.name}
        </Typography>
        {!product.nameArabic || !product.nameHebrew ? (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            onClick={onEdit}
            label={
              !product.nameArabic && !product.nameHebrew
                ? "Needs Arabic & Hebrew"
                : !product.nameArabic
                  ? "Needs Arabic"
                  : "Needs Hebrew"
            }
            sx={{ mt: 0.5, height: 22 }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {product.nameArabic} · {product.nameHebrew}
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", mt: 0.5, flexWrap: "wrap" }}>
          <Typography variant="subtitle1" fontWeight={700} color={hasDiscount ? "error.main" : "text.primary"}>
            {formatCurrency(hasDiscount ? product.discountPrice : product.price)}
          </Typography>
          {hasDiscount && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                {formatCurrency(product.price)}
              </Typography>
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={
                  product.discountType === DISCOUNT_TYPE_PERCENTAGE && product.discountPercent != null
                    ? `-${product.discountPercent}%`
                    : product.discountType === DISCOUNT_TYPE_FIXED
                      ? "Fixed discount"
                      : "Discount"
                }
                sx={{ height: 20 }}
              />
            </>
          )}
        </Stack>

        {product.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-line" }}>
            {product.description}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1, fontStyle: "italic" }}>
            No description
          </Typography>
        )}

        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75, mt: 1.5 }}>
          <Chip size="small" variant="outlined" label={`Stock: ${product.stockQuantity}`} />
          {product.sku && <Chip size="small" variant="outlined" label={`SKU: ${product.sku}`} />}
          {product.wholesalePrice != null && (
            <Chip size="small" variant="outlined" label={`Wholesale: ${formatCurrency(product.wholesalePrice)}`} />
          )}
          {product.authenticityValue && <Chip size="small" variant="outlined" label={product.authenticityValue} />}
          {product.requiresSpecialDelivery && <Chip size="small" color="warning" variant="outlined" label="Special delivery" />}
          {!product.isActive && <Chip size="small" color="default" label="Hidden by store" />}
          {product.videoUrl && (
            <Chip size="small" variant="outlined" label={`Video · ${VIDEO_PLACEMENT_LABELS[product.videoPlacement] || ""}`} />
          )}
        </Stack>

        {attributes.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase" }}>
              Details
            </Typography>
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
              {attributes.map((a) => (
                <Chip key={a.name} size="small" label={formatAttribute(a)} />
              ))}
            </Stack>
          </Box>
        )}

        {variants.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase" }}>
              Variants ({variants.length})
            </Typography>
            <Stack
              spacing={0.75}
              sx={{ mt: 0.5, maxHeight: 260, overflowY: "auto", pr: 0.5 }}
            >
              {variants.map((v) => {
                const variantDiscount = v.discountPrice != null && v.discountPrice < v.price;
                return (
                  <Stack
                    key={v.variantId}
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      p: 0.75,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      opacity: v.isActive ? 1 : 0.55,
                    }}
                  >
                    <Avatar src={v.imageUrl || undefined} variant="rounded" sx={{ width: 40, height: 40, bgcolor: "background.default" }}>
                      <ImageNotSupportedOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {v.variantName}
                        {!v.isActive ? " (inactive)" : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
                        {[...(v.attributes || []).map(formatAttribute), v.sku ? `SKU ${v.sku}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight={700} color={variantDiscount ? "error.main" : "text.primary"}>
                        {formatCurrency(variantDiscount ? v.discountPrice : v.price)}
                      </Typography>
                      <Typography variant="caption" color={v.stockQuantity > 0 ? "text.secondary" : "error.main"}>
                        Stock {v.stockQuantity}
                      </Typography>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          Submitted {formatDate(product.updatedAt || product.createdAt)}
        </Typography>
        {isRejected && product.approvalRejectionReason && (
          <Alert severity="error" icon={<CancelRoundedIcon fontSize="small" />} sx={{ mt: 1.5, py: 0 }}>
            {product.approvalRejectionReason}
          </Alert>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: "wrap" }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<EditOutlinedIcon />}
          disabled={busy || opening}
          onClick={onEdit}
          sx={{ ml: "0 !important" }}
        >
          Edit before publishing
        </Button>
        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={<CheckCircleRoundedIcon />}
            disabled={busy}
            onClick={onApprove}
          >
            Approve
          </Button>
          {!isRejected && (
            <Button fullWidth variant="outlined" color="error" startIcon={<CancelRoundedIcon />} disabled={busy} onClick={onReject}>
              Reject
            </Button>
          )}
        </Stack>
      </CardActions>
    </Card>
  );
}

function RejectDialog({ product, busy, onClose, onSubmit }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (product) setReason("");
  }, [product]);

  return (
    <Dialog open={Boolean(product)} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reject "{product?.name}"</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The store owner will be notified with this reason. When they edit the product it goes back into the review queue.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Reason"
          placeholder="e.g. Photos are blurry, please upload clear images on a plain background."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 500 } }}
          helperText={`${reason.length}/500`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" color="error" disabled={busy || !reason.trim()} onClick={() => onSubmit(reason.trim())}>
          Reject product
        </Button>
      </DialogActions>
    </Dialog>
  );
}
